import { useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { useFeatureIsOn } from "@/app/featureFlags";
import { Button } from "@/components/Button/Button";
import { Image } from "@/components/Image/Image";
import { usePaymentContext } from "@/contexts/PaymentContext";
import { RoomDetailsDrawer } from "@/features/hotels/components/RoomSelection/RoomDetailsDrawer";
import {
  MealFeature,
  RoomFeature,
} from "@/features/hotels/components/RoomSelection/RoomFeature";
import { RoomGalleryButton } from "@/features/hotels/components/RoomSelection/RoomGalleryButton";
import type {
  HotelBookingRoomOption,
  HotelData,
  HotelOfferRoom,
} from "@/features/hotels/types/types";
import { createRoomCombinations } from "@/features/hotels/utils/roomCombinations";
import { includeBnplFee, calculateMonthlyInstallment } from "@/utils/bnpl-fee";
import { formatCurrency } from "@/utils/currency";

type Props = {
  hotel: HotelData;
  rooms: HotelOfferRoom[];
  guestCount: number;
  bookingNights: number;
  onRoomSelected: (option: HotelBookingRoomOption[]) => void;
  onRoomGalleryClick: (room: HotelBookingRoomOption, index: number) => void;
};

export const HotelRoomSelection = ({
  hotel,
  rooms,
  guestCount,
  bookingNights,
  onRoomSelected,
  onRoomGalleryClick,
}: Props) => {
  const intl = useIntl();
  const [selectedRoomsDetails, setSelectedRoomsDetails] = useState<
    HotelBookingRoomOption[] | null
  >(null);
  const { maxAvailableInstallments, isEligible, isEligibilityLoading } =
    usePaymentContext();
  const isBnplFeatureEnabled = useFeatureIsOn("bnpl") && Boolean(isEligible);

  const combinationGroups = useMemo(
    () => createRoomCombinations(rooms),
    [rooms],
  );

  return (
    <>
      <ul className="space-y-3">
        {combinationGroups.map((roomGroup) => {
          const rooms = roomGroup.combinations.flatMap((c) => c.rooms);
          const firstRoomWithImageIndex = rooms.findIndex(
            (r) => r.images && r.images.length > 0,
          );
          const firstRoomWithImage =
            firstRoomWithImageIndex !== -1
              ? rooms[firstRoomWithImageIndex]
              : null;

          const mainImage = firstRoomWithImage
            ? firstRoomWithImage.images?.[0]
            : null;

          return (
            <li
              key={roomGroup.id}
              className="rounded-3xl bg-white pb-4 text-text-primary"
            >
              <div className="mb-3 flex p-1">
                {firstRoomWithImage && mainImage ? (
                  <RoomGalleryButton
                    onClick={() =>
                      onRoomGalleryClick(
                        firstRoomWithImage,
                        firstRoomWithImageIndex,
                      )
                    }
                    thumbnailImage={mainImage}
                    imageCount={firstRoomWithImage?.images?.length || 0}
                  />
                ) : (
                  <Image
                    className="h-30 w-22.5 shrink-0 rounded-2xl object-cover"
                    src="/imagePlaceholder.svg"
                    alt={intl.formatMessage({
                      defaultMessage: "No image available",
                      description:
                        "Fallback alt text shown when no hotel or room image is available",
                    })}
                  />
                )}

                <div className="overflow-hidden p-3">
                  <div className="mb-1 line-clamp-2 text-lg font-medium">
                    {roomGroup.displayName}
                  </div>

                  <div className="mb-2 text-sm text-text-tertiary">
                    {intl.formatMessage(
                      {
                        defaultMessage:
                          "{guests, plural, one {# guest} other {# guests}}, {nights, plural, one {# night} other {# nights}}",
                        description:
                          "Summary of number of guests and nights in a hotel booking card",
                      },
                      {
                        guests: guestCount,
                        nights: bookingNights,
                      },
                    )}
                  </div>

                  <Button
                    size="small"
                    variant="textFit"
                    onClick={() =>
                      setSelectedRoomsDetails(roomGroup.combinations[0].rooms)
                    }
                  >
                    <span className="font-medium">
                      {intl.formatMessage({
                        defaultMessage: "Room details",
                        description:
                          "Section heading for room details and description",
                      })}
                    </span>
                  </Button>
                </div>
              </div>
              <ul className="flex w-full snap-x snap-mandatory flex-nowrap gap-4 overflow-x-auto px-3">
                {roomGroup.combinations.map((combination) => (
                  <li
                    key={combination.id}
                    className="flex w-55 shrink-0 snap-center flex-col rounded-2xl border border-stroke-neutral-soft p-4"
                  >
                    <ul className="mb-6 space-y-2">
                      <li>
                        {combination.isRefundable ? (
                          <RoomFeature
                            label={intl.formatMessage({
                              defaultMessage: "Refundable",
                              description:
                                "Badge on a room option indicating it is refundable",
                            })}
                            isIncluded
                          />
                        ) : (
                          <RoomFeature
                            label={intl.formatMessage({
                              defaultMessage: "Non-refundable",
                              description:
                                "Badge on a room option indicating it is non-refundable",
                            })}
                            isIncluded={false}
                          />
                        )}
                      </li>
                      <li>
                        <MealFeature board={combination.boardBasis} />
                      </li>
                    </ul>

                    <div className="mt-auto mb-4">
                      <div className="w-full overflow-hidden text-base font-bold overflow-ellipsis">
                        {formatCurrency(
                          combination.totalPrice,
                          combination.currency,
                        )}
                      </div>
                      {isBnplFeatureEnabled && (
                        <div className="overflow-hidden text-sm font-medium overflow-ellipsis">
                          {intl.formatMessage(
                            {
                              defaultMessage: "or {price} / monthly",
                              description:
                                "Alternative price shown as a monthly installment figure",
                            },
                            {
                              price: formatCurrency(
                                calculateMonthlyInstallment(
                                  includeBnplFee(combination.totalPrice),
                                  maxAvailableInstallments,
                                ),
                                combination.currency,
                              ),
                            },
                          )}
                        </div>
                      )}
                      <div className="mt-1 text-xs text-text-tertiary">
                        {intl.formatMessage({
                          defaultMessage: "Total with taxes and fees",
                          description:
                            "Label for the total price including all taxes and fees",
                        })}
                      </div>
                    </div>

                    <Button
                      type="button"
                      variant="contained"
                      fullWidth
                      onClick={() => {
                        onRoomSelected(combination.rooms);
                      }}
                      loading={isEligibilityLoading}
                    >
                      {intl.formatMessage({
                        defaultMessage: "Select",
                        description: "Generic 'Select' action label",
                      })}
                    </Button>
                  </li>
                ))}
              </ul>
            </li>
          );
        })}
      </ul>

      <RoomDetailsDrawer
        hotel={hotel}
        rooms={selectedRoomsDetails}
        onClose={() => setSelectedRoomsDetails(null)}
      />
    </>
  );
};
